import apiClient from '@/api/client'
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

export interface User {
  id: number
  name: string
  email: string
  roles: string[]
  firstName?: string
  lastName?: string
  fullName?: string
  phone?: string
  lastPasswordChange?: string | null
  twoFactorEnabled?: boolean
  company?: {
    id: number
    name: string
    inn?: string
    fullName?: string
    kpp?: string
    ogrn?: string
    legalAddress?: string
    actualAddress?: string
    director?: string
    phone?: string
    email?: string
    isVerified?: boolean
    creditLimit?: string
    credit_limit?: string
  }
}

interface UpdateProfileData {
  firstName: string
  lastName: string
  email: string
  phone: string
}

interface CompanyData {
  name: string
  fullName: string
  inn: string
  kpp: string
  ogrn: string
  legalAddress: string
  actualAddress: string
  director: string
  phone: string
  email: string
}

interface ChangePasswordData {
  currentPassword: string
  newPassword: string
}

interface RegisterData {
  firstName: string
  lastName: string
  email: string
  password: string
  inn: string
  companyName: string
}

interface ApiValidationErrorItem {
  field?: string
  message?: string
}

interface ApiErrorPayload {
  message?: string
  errors?: ApiValidationErrorItem[]
}

interface LoginResult {
  success: boolean
  error?: string
  requiresTwoFactor?: boolean
  challengeToken?: string
  challengeExpiresAt?: string
  challengeEmail?: string
}

interface ActionResult {
  success: boolean
  error?: string
  message?: string
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string | null>(null)
  const user = ref<User | null>(null)
  const loading = ref(false)
  const initialized = ref(false)
  let initAuthPromise: Promise<void> | null = null

  // Getters
  const isAuthenticated = computed(() => {
    const result = !!token.value
    return result
  })
  const isBuyer = computed(() => {
    const roles = user.value?.roles ?? []
    return (
      roles.includes('ROLE_USER') &&
      !roles.includes('ROLE_SUPPLIER') &&
      !roles.includes('ROLE_ADMIN')
    )
  })
  const isSupplier = computed(() => user.value?.roles?.includes('ROLE_SUPPLIER') ?? false)
  const isAdmin = computed(() => user.value?.roles?.includes('ROLE_ADMIN') ?? false)
  const company = computed(() => user.value?.company ?? null)

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken
  }

  const setUser = (newUser: User & Record<string, unknown>) => {
    // Normalize snake_case fields from backend → camelCase
    const firstName = (newUser.firstName ?? newUser['first_name'] ?? '') as string
    const lastName = (newUser.lastName ?? newUser['last_name'] ?? '') as string
    const phone = (newUser.phone ?? '') as string
    const company = (newUser.company ?? newUser['firm'] ?? null) as User['company'] | null
    const fullName = (newUser.fullName ?? newUser['full_name']) as string | undefined
    const twoFactorEnabled = (newUser.twoFactorEnabled ??
      newUser['two_factor_enabled'] ??
      false) as boolean

    const normalized: User = {
      id: newUser.id as number,
      // Бэкенд может не отдавать name (login отдаёт full_name + first/last_name)
      name:
        ((newUser.name as string | undefined) ?? fullName ?? `${firstName} ${lastName}`.trim()) ||
        (newUser.email as string),
      email: newUser.email as string,
      roles: Array.isArray(newUser.roles) ? (newUser.roles as string[]) : [],
      firstName,
      lastName,
      phone,
      lastPasswordChange: (newUser.lastPasswordChange as string | null | undefined) ?? null,
      twoFactorEnabled,
    }

    if (typeof fullName === 'string') normalized.fullName = fullName
    if (company !== null && company !== undefined) normalized.company = company

    user.value = normalized
  }

  const completeAuthenticatedSession = async (data: Record<string, unknown>) => {
    const receivedToken =
      (data['meta'] as { token?: string } | undefined)?.token ??
      (data['data'] as { token?: string } | undefined)?.token ??
      (data['token'] as string | undefined)

    if (!receivedToken) throw new Error('Authentication failed')

    setToken(receivedToken)

    const payload = (data['data'] ?? data) as {
      user?: User & Record<string, unknown>
      company?: User['company']
    }

    if (payload.user) {
      setUser({
        ...payload.user,
        ...(payload.company ? { company: payload.company } : {}),
      })
    } else {
      await fetchUser()
    }

    try {
      const { mergeGuestFavoritesOnLogin } = await import('@/modules/profile/public')
      await mergeGuestFavoritesOnLogin()
    } catch (favoritesError) {
      console.warn('⚠️ Favorites refresh failed:', favoritesError)
    }

    try {
      const { syncCartOnAuthChange } = await import('@/modules/order/public')
      const cartInitResult = await syncCartOnAuthChange()

      if (cartInitResult.warnings.length > 0) {
        const { useAppNotifications } = await import('@/modules/common/public')
        const notifications = useAppNotifications()
        const message =
          cartInitResult.warnings.length === 1
            ? (cartInitResult.warnings[0] ?? 'Не удалось перенести товар в корзину после входа')
            : `Не удалось перенести ${cartInitResult.warnings.length} товар(а) в корзину после входа`

        notifications.showError(message)
      }
    } catch (cartError) {
      console.warn('⚠️ Cart refresh failed:', cartError)
    }
  }

  const login = async (credentials: { email: string; password: string }): Promise<LoginResult> => {
    loading.value = true

    try {
      const { data } = await apiClient.post('/api/auth/login', credentials)

      if (data?.data?.requires_two_factor) {
        const result: LoginResult = { success: false, requiresTwoFactor: true }
        const challengeToken = data.data.challenge_token as string | undefined
        const challengeExpiresAt = data.data.expires_at as string | undefined
        const challengeEmail = data.data.email as string | undefined
        if (challengeToken !== undefined) result.challengeToken = challengeToken
        if (challengeExpiresAt !== undefined) result.challengeExpiresAt = challengeExpiresAt
        if (challengeEmail !== undefined) result.challengeEmail = challengeEmail
        return result
      }

      await completeAuthenticatedSession(data as Record<string, unknown>)

      return { success: true }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = (error.response?.data ?? null) as ApiErrorPayload | null
        if (responseData?.message) {
          return { success: false, error: responseData.message }
        }
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ошибка входа',
      }
    } finally {
      loading.value = false
    }
  }

  const verifyTwoFactor = async (payload: {
    challengeToken: string
    code: string
  }): Promise<LoginResult> => {
    loading.value = true

    try {
      const { data } = await apiClient.post('/api/auth/two-factor/verify', {
        challenge_token: payload.challengeToken,
        code: payload.code,
      })

      await completeAuthenticatedSession(data as Record<string, unknown>)

      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ошибка подтверждения входа',
      }
    } finally {
      loading.value = false
    }
  }

  const logout = () => {
    token.value = null
    user.value = null

    import('@/modules/profile/public')
      .then(({ clearFavoritesOnLogout }) => {
        clearFavoritesOnLogout()
      })
      .catch(() => {
        // Игнорируем ошибки при очистке избранного
      })

    import('@/modules/order/public')
      .then(({ syncCartOnAuthChange }) => syncCartOnAuthChange())
      .catch(() => {
        // Игнорируем ошибки при очистке корзины
      })
  }

  /**
   * Выход по действию пользователя: отзывает refresh-токены на сервере и чистит cookie.
   * Без этого refresh_token-cookie остаётся действующим, и ближайшая перезагрузка страницы
   * молча восстанавливает сессию. Внутренний `logout()` (401, провал refresh) сервер не зовёт.
   */
  const signOut = async () => {
    try {
      if (token.value) {
        await apiClient.post('/api/auth/logout')
      }
    } catch {
      // Сервер недоступен или токен уже недействителен - локальный выход всё равно выполняем
    } finally {
      // Только явный выход: внутренний logout() срабатывает и у гостя на 401 от refresh,
      // там гостевую корзину стирать нельзя.
      await import('@/modules/order/public')
        .then(({ discardUnsyncedGuestCart }) => discardUnsyncedGuestCart())
        .catch(() => {
          // Корзину почистит следующий успешный перенос
        })
      logout()
    }
  }

  const fetchUser = async () => {
    if (!token.value) return

    try {
      const { data } = await apiClient.get('/api/auth/user')
      const rawUser = data?.data?.user ?? data?.data ?? data?.user
      const rawCompany = data?.data?.company ?? data?.company
      if (rawUser) {
        setUser({
          ...rawUser,
          ...(rawCompany ? { company: rawCompany } : {}),
        })
      }
    } catch {
      logout()
    }
  }

  const fetchProfile = async () => {
    return fetchUser()
  }

  const updateProfile = async (profileData: UpdateProfileData): Promise<void> => {
    await apiClient.put('/api/profile', profileData)
    if (user.value) {
      user.value = {
        ...user.value,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        name: `${profileData.firstName} ${profileData.lastName}`.trim(),
      }
    }
  }

  const updateCompany = async (companyData: CompanyData): Promise<void> => {
    await apiClient.put('/api/profile/company', companyData)
    if (user.value) {
      const currentCompany = user.value.company
      const updatedCompany = {
        id: currentCompany?.id ?? 0,
        name: companyData.name,
        fullName: companyData.fullName,
        inn: companyData.inn,
        kpp: companyData.kpp,
        ogrn: companyData.ogrn,
        legalAddress: companyData.legalAddress,
        actualAddress: companyData.actualAddress,
        director: companyData.director,
        phone: companyData.phone,
        email: companyData.email,
        isVerified: currentCompany?.isVerified ?? false,
        ...(typeof currentCompany?.creditLimit === 'string'
          ? { creditLimit: currentCompany.creditLimit }
          : {}),
        ...(typeof currentCompany?.credit_limit === 'string'
          ? { credit_limit: currentCompany.credit_limit }
          : {}),
      }

      user.value = {
        ...user.value,
        company: updatedCompany,
      }
    }
  }

  const changePassword = async (passwordData: ChangePasswordData): Promise<void> => {
    await apiClient.put('/api/profile/password', passwordData)
    if (user.value) {
      user.value = {
        ...user.value,
        lastPasswordChange: new Date().toISOString(),
      }
    }
  }

  const toggleTwoFactor = async (enabled: boolean): Promise<void> => {
    await apiClient.put('/api/profile/two-factor', { enabled })
    if (user.value) {
      user.value = {
        ...user.value,
        twoFactorEnabled: enabled,
      }
    }
  }

  const createCompany = async (companyData: CompanyData): Promise<void> => {
    await apiClient.post('/api/profile/company', companyData)
    if (user.value) {
      user.value = {
        ...user.value,
        company: {
          id: user.value.company?.id ?? 0,
          name: companyData.name,
          fullName: companyData.fullName,
          inn: companyData.inn,
          kpp: companyData.kpp,
          ogrn: companyData.ogrn,
          legalAddress: companyData.legalAddress,
          actualAddress: companyData.actualAddress,
          director: companyData.director,
          phone: companyData.phone,
          email: companyData.email,
          isVerified: false,
        },
      }
    }
  }

  const hasRole = (role: string): boolean => {
    return user.value?.roles?.includes(role) ?? false
  }

  const hasAnyRole = (roles: string[]): boolean => {
    const user_roles = user.value?.roles ?? []
    if (!Array.isArray(user_roles)) return false
    return roles.some((role) => user_roles.includes(role))
  }

  const register = async (payload: RegisterData): Promise<ActionResult> => {
    loading.value = true
    try {
      const { data } = await apiClient.post('/api/auth/register', {
        email: payload.email,
        password: payload.password,
        inn: payload.inn,
        first_name: payload.firstName,
        last_name: payload.lastName,
        company_name: payload.companyName,
      })
      return {
        success: true,
        message:
          (data?.message as string | undefined) ??
          'Регистрация завершена. Проверьте email для подтверждения аккаунта',
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const responseData = (error.response?.data ?? null) as ApiErrorPayload | null
        const fieldLabels: Record<string, string> = {
          email: 'Email',
          password: 'Пароль',
          first_name: 'Имя',
          last_name: 'Фамилия',
          inn: 'ИНН',
          company_name: 'Название компании',
        }

        const errors = Array.isArray(responseData?.errors) ? responseData.errors : []
        const normalizedMessages = errors
          .map((item) => {
            const message = item?.message?.trim()
            if (!message) return ''

            const field = item?.field?.trim()
            if (!field) return message

            const label = fieldLabels[field] ?? field
            const hasFieldInMessage = message.includes(field) || message.includes(`'${field}'`)
            if (hasFieldInMessage) {
              return message.replaceAll(`'${field}'`, `"${label}"`).replaceAll(field, label)
            }

            return `${label}: ${message}`
          })
          .filter((message) => message.length > 0)

        if (normalizedMessages.length > 0) {
          return {
            success: false,
            error: normalizedMessages.join('; '),
          }
        }

        if (responseData?.message) {
          return { success: false, error: responseData.message }
        }
      }

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ошибка регистрации',
      }
    } finally {
      loading.value = false
    }
  }

  const resendEmailVerification = async (email: string): Promise<ActionResult> => {
    loading.value = true
    try {
      const { data } = await apiClient.post('/api/auth/resend-verification', { email })
      return {
        success: true,
        message: (data?.message as string | undefined) ?? 'Письмо для подтверждения отправлено',
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ошибка отправки письма',
      }
    } finally {
      loading.value = false
    }
  }

  const requestPasswordReset = async (email: string): Promise<ActionResult> => {
    try {
      const { data } = await apiClient.post('/api/auth/forgot-password', { email })
      return {
        success: true,
        message:
          (data?.message as string | undefined) ??
          'Если аккаунт существует, письмо для сброса пароля отправлено',
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Не удалось отправить письмо',
      }
    }
  }

  const resetPassword = async (token: string, password: string): Promise<ActionResult> => {
    try {
      const { data } = await apiClient.post('/api/auth/reset-password', { token, password })
      return {
        success: true,
        message: (data?.message as string | undefined) ?? 'Пароль успешно изменён',
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Не удалось изменить пароль',
      }
    }
  }

  const initAuth = async () => {
    if (initialized.value) {
      return
    }
    if (initAuthPromise) {
      await initAuthPromise
      return
    }

    initAuthPromise = (async () => {
      try {
        const { data } = await apiClient.post('/api/auth/refresh')
        const refreshedToken = data?.meta?.token ?? data?.data?.token ?? data?.token
        if (!refreshedToken) {
          return
        }

        setToken(refreshedToken)
        await fetchUser()

        // Same as login: restore server cart after cookie-based session refresh.
        // Otherwise App shell may have bootstrapped an empty guest cart first.
        try {
          const { syncCartOnAuthChange } = await import('@/modules/order/public')
          await syncCartOnAuthChange()
        } catch (cartError) {
          console.warn('⚠️ Cart refresh after session restore failed:', cartError)
        }
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          logout()
          return
        }

        console.warn('Failed to restore auth session:', error)
        logout()
      } finally {
        initialized.value = true
        initAuthPromise = null
      }
    })()

    await initAuthPromise
  }

  return {
    // State
    token,
    user,
    loading,
    initialized,

    // Getters
    isAuthenticated,
    isBuyer,
    isSupplier,
    isAdmin,
    company,

    // Actions
    setToken,
    setUser,
    login,
    register,
    resendEmailVerification,
    requestPasswordReset,
    resetPassword,
    logout,
    signOut,
    fetchUser,
    fetchProfile,
    updateProfile,
    updateCompany,
    changePassword,
    toggleTwoFactor,
    createCompany,
    verifyTwoFactor,
    hasRole,
    hasAnyRole,
    initAuth,
  }
})
